import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ContentSyncCheckbox,
  ContentSyncState,
  ContentSyncToolbar,
  useContentSync
} from "../components/admin/ContentSyncControls";

const DIFFICULTIES = ["LOT", "MOT", "HOT"];

function AdminChapters({ apiBase, token }) {
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [frames, setFrames] = useState([]);
  const [graphs, setGraphs] = useState([]);
  const [filter, setFilter] = useState("");
  const [notice, setNotice] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const ROWS_PER_PAGE = 25;

  useEffect(() => {
    let mounted = true;
    async function loadChapters() {
      if (!token) return;
      setLoading(true);
      try {
        const [chaptersRes, framesRes, graphsRes] = await Promise.all([
          fetch(`${apiBase}/api/admin/chapters`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiBase}/api/admin/frames`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          fetch(`${apiBase}/api/admin/solution-graphs`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);

        if (!chaptersRes.ok || !framesRes.ok || !graphsRes.ok) {
          throw new Error("Failed to load chapter metrics");
        }

        const [chaptersPayload, framesPayload, graphsPayload] = await Promise.all([
          chaptersRes.json(),
          framesRes.json(),
          graphsRes.json()
        ]);

        if (mounted) {
          setChapters(chaptersPayload.chapters || []);
          setFrames(framesPayload.frames || []);
          setGraphs(graphsPayload.graphs || []);
        }
      } catch (err) {
        if (mounted) {
          setNotice({ type: "error", text: err.message || "Failed to load chapters" });
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadChapters();
    return () => {
      mounted = false;
    };
  }, [apiBase, token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  const metricsByChapterDifficulty = useMemo(() => {
    const map = {};

    frames.forEach((frame) => {
      const chapterId = String(frame?.chapterId || "");
      const difficulty = String(frame?.difficulty || "");
      if (!chapterId || !difficulty) return;
      if (!map[chapterId]) map[chapterId] = {};
      if (!map[chapterId][difficulty]) map[chapterId][difficulty] = { frames: 0, graphs: 0 };
      map[chapterId][difficulty].frames += 1;
    });

    graphs.forEach((graph) => {
      const chapterId = String(graph?.frameId?.chapterId || "");
      const difficulty = String(graph?.frameId?.difficulty || "");
      if (!chapterId || !difficulty) return;
      if (!map[chapterId]) map[chapterId] = {};
      if (!map[chapterId][difficulty]) map[chapterId][difficulty] = { frames: 0, graphs: 0 };
      map[chapterId][difficulty].graphs += 1;
    });

    return map;
  }, [frames, graphs]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return chapters;
    return chapters.filter(
      (c) =>
        String(c.chapterId || "").toLowerCase().includes(q) ||
        String(c.title || "").toLowerCase().includes(q) ||
        String(c.grade || "").toLowerCase().includes(q)
    );
  }, [chapters, filter]);

  const syncItems = useMemo(
    () => chapters.map((chapter) => ({ contentType: "chapter", contentId: String(chapter.chapterId) })),
    [chapters]
  );
  const contentSync = useContentSync({ apiBase, token, items: syncItems });
  const visibleSyncItems = useMemo(
    () => filtered.map((chapter) => ({ contentType: "chapter", contentId: String(chapter.chapterId) })),
    [filtered]
  );

  const totalPages = Math.ceil(filtered.length / ROWS_PER_PAGE);
  const paginatedChapters = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    const end = start + ROWS_PER_PAGE;
    return filtered.slice(start, end);
  }, [filtered, currentPage, ROWS_PER_PAGE]);

  return (
    <div className="admin-panel">
      <div className="admin-toolbar">
        <input
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
          placeholder="Filter by chapter id or name"
        />
        {contentSync.canAuthorContent && (
          <button className="primary-button" type="button" onClick={() => navigate("/admin/chapters/new")}>
            Create Chapter
          </button>
        )}
      </div>

      <ContentSyncToolbar sync={contentSync} visibleItems={visibleSyncItems} />

      {notice.text && (
        <div className={`admin-notice ${notice.type === "error" ? "error" : "success"}`}>{notice.text}</div>
      )}

      {filtered.length > 0 && (
        <div className="pagination-controls">
          <button className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Prev
          </button>

          <span className="pagination-info">
            Page {currentPage} of {totalPages}
            &nbsp;·&nbsp;
            {filtered.length} rows total
          </span>

          <button className="pagination-btn"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next →
          </button>
        </div>
      )}

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              {contentSync.showSync && <th aria-label="Select for production sync" />}
              <th>S.No</th>
              <th>Chapter ID</th>
              <th>Chapter Name</th>
              <th>Grade</th>
              <th>Order</th>
              <th>Status</th>
              {contentSync.showSync && <th>Production</th>}
              <th>LOT Frames</th>
              <th>LOT Graphs</th>
              <th>MOT Frames</th>
              <th>MOT Graphs</th>
              <th>HOT Frames</th>
              <th>HOT Graphs</th>
            </tr>
          </thead>
          <tbody>
            {paginatedChapters.map((item, index) => {
              const chapterMetrics = metricsByChapterDifficulty[String(item.chapterId || "")] || {};
              const syncItem = { contentType: "chapter", contentId: String(item.chapterId) };
              return (
                <tr
                  key={item.chapterId}
                  className={contentSync.isSelected(syncItem) ? "selected" : ""}
                  onClick={() =>
                    navigate(`/admin/chapters/${item.chapterId}`, {
                      state: { chapter: item }
                    })
                  }
                >
                  {contentSync.showSync && (
                    <td onClick={(event) => event.stopPropagation()}>
                      <ContentSyncCheckbox
                        sync={contentSync}
                        item={syncItem}
                        label={`Select chapter ${item.title} for production sync`}
                      />
                    </td>
                  )}
                  <td>{(currentPage - 1) * ROWS_PER_PAGE + index + 1}</td>
                  <td>{item.chapterId}</td>
                  <td>{item.title}</td>
                  <td>{item.grade || "Grade 9"}</td>
                  <td>{item.order || 0}</td>
                  <td>{item.active === false ? "Inactive" : "Active"}</td>
                  {contentSync.showSync && (
                    <td><ContentSyncState sync={contentSync} item={syncItem} /></td>
                  )}
                  {DIFFICULTIES.flatMap((difficulty) => [
                    <td key={`${item.chapterId}-${difficulty}-frames`}>
                      {chapterMetrics[difficulty]?.frames || 0}
                    </td>,
                    <td key={`${item.chapterId}-${difficulty}-graphs`}>
                      {chapterMetrics[difficulty]?.graphs || 0}
                    </td>
                  ])}
                </tr>
              );
            })}
            {loading && (
              <tr>
                <td colSpan={contentSync.showSync ? 14 : 12}>Loading chapters...</td>
              </tr>
            )}
            {!filtered.length && !loading && (
              <tr>
                <td colSpan={contentSync.showSync ? 14 : 12}>No chapters found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AdminChapters;
